import * as React from 'react';
import { classNames, useUid } from '@/utils';

export interface FileFieldProps {
  /** The HTML ID attribute to be applied to the file upload field input element (optional). */
  id?: string;
  /** The name attribute to be applied to the file upload field input element (optional). */
  name?: string;
  /**
   * The accept attribute to be applied to the file upload field input element (optional).
   * Specifies the types of accepted files.
   */
  accept?: string;
  /**
   * The size of the file upload field (optional).
   * - 'small': render a smaller file upload field
   * - 'medium': render a larger file upload field
   */
  size?: 'small' | 'medium';
  /** Whether the file upload field should be disabled (optional). */
  disabled?: boolean;
  /** The default value of the file upload field input (used to display a preview image when type is image). */
  defaultValue?: string | null;
  /**
   * A function to be called when the value of the file upload field changes (optional).
   * The function receives the new file value of the input as a `File` object, or `null` if no file is selected.
   */
  onChange?(value: File | null): void;
  /** The label to be displayed above the file upload field (optional). */
  label?: React.ReactNode;
  /**
   * The type of file to be uploaded (optional).
   * - 'image': upload an image file
   * - 'file': upload any file type
   */
  type?: 'image' | 'file';
  /** Help text to be displayed below the file upload field (optional). */
  helpText?: React.ReactNode;
  /** CSS class names to be applied to the file upload field input element (optional). */
  inputClassName?: string;
}

function EmptyMarkupPlaceholder({ type, defaultValue }: { type: 'image' | 'file'; defaultValue?: string | null }) {
  if (type === 'file')
    return (
      <>
        <i className="fa-regular fa-file-csv" />
        <span className="filename">No file added</span>
      </>
    );

  if (defaultValue) {
    return <img src={defaultValue} alt="" />;
  }

  return (
    <div className="image-placeholder">
      <i className="fa-regular fa-image" />
    </div>
  );
}

function Preview({ type, file }: { type: string; file: File }) {
  if (type === 'image' && file) {
    const imageUrl = URL.createObjectURL(file);
    return <img src={imageUrl} alt={file.name} />;
  }

  return <span className="filename">{file?.name}</span>;
}

export const FileField = React.forwardRef<HTMLInputElement, FileFieldProps>((props, ref) => {
  const {
    id,
    label,
    name,
    disabled,
    helpText,
    inputClassName,
    onChange,
    type = 'image',
    accept = type === 'image' ? 'image/*' : '.csv, .xls',
    size = 'small',
    defaultValue,
  } = props;
  const uid = useUid(id);
  const [value, setValue] = React.useState<File | null>(null);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(event.currentTarget.files?.[0] || null);
    onChange?.(event.currentTarget.files?.[0] || null);
  };

  return (
    <label htmlFor={uid}>
      <p>{label}</p>
      <div className={classNames('file-uploader', size, { uploaded: value })}>
        <div className={classNames({ 'file-preview': type === 'file', 'image-preview': type === 'image' })}>
          {!value && <EmptyMarkupPlaceholder type={type} defaultValue={defaultValue} />}
          {value && <Preview type={type} file={value} />}
        </div>
        <div className="file-input">
          <span>
            <i className="fa-regular fa-arrow-up-from-bracket" /> Upload a file
          </span>
          <input
            id={uid}
            name={name}
            ref={ref}
            type="file"
            disabled={disabled}
            accept={accept}
            onChange={handleChange}
            className={inputClassName}
          />
        </div>
      </div>
      <small>{helpText}</small>
    </label>
  );
});

FileField.displayName = 'FileField';
